import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();               // ← added navigation hook
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * Scrolls to a section on the page.
   * If we are not on the home page, we navigate there first
   * and then attempt the scroll after a short delay.
   */
  const scrollToSection = (sectionId: string) => {
    const scroll = () => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        console.warn(`Element with id "${sectionId}" not found.`);
      }
    };

    if (location.pathname !== '/') {
      // useNavigate is the correct way to programmatically change route
      navigate('/');
      // give the new page a moment to render before scrolling
      setTimeout(scroll, 100);
    } else {
      scroll();
    }
  };

  const getNavTextColor = () => {
    if (isHomePage) {
      return isScrolled ? 'text-[#222222]' : 'text-white';
    }
    return 'text-[#222222]';
  };

  const getNavHoverColor = () => {
    return 'hover:text-[#009663]';
  };

  return (
    <>
      {/* PC Header - exactly v120 */}
      <header
        className={`hidden md:block fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomePage ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
              <i className="ri-leaf-line text-white text-base"></i>
            </div>
            <span
              className={`text-xl font-bold transition-colors hover:text-[#009663] ${
                isScrolled || !isHomePage ? 'text-[#222222]' : 'text-white'
              }`}
            >
              KW테크
            </span>
          </Link>

          <nav className="flex items-center gap-8">
            <Link
              to="/about"
              className={`text-[15px] font-medium transition-colors ${getNavTextColor()} ${getNavHoverColor()}`}
            >
              회사소개
            </Link>
            <Link
              to="/products"
              className={`text-[15px] font-medium transition-colors ${getNavTextColor()} ${getNavHoverColor()}`}
            >
              제품소개
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-[15px] font-medium transition-colors cursor-pointer ${getNavTextColor()} ${getNavHoverColor()}`}
            >
              오시는길
            </button>
            <Link
              to="/inquiry"
              className="bg-primary text-white px-5 py-2 rounded-full text-[15px] font-medium hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
            >
              견적 문의
            </Link>
          </nav>
        </div>
      </header>

      {/* Mobile Header - separate from slider, white bg, black text */}
      <header className="md:hidden sticky top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
              <i className="ri-leaf-line text-white text-sm"></i>
            </div>
            <span className="text-lg font-bold text-[#222222]">KW테크</span>
          </Link>

          <div className="flex items-center overflow-x-auto mobile-nav-scroll gap-5 ml-4 touch-pan-x">
            <Link
              to="/about"
              className="text-[13px] font-medium whitespace-nowrap shrink-0 text-[#222222]"
            >
              회사소개
            </Link>
            <Link
              to="/products"
              className="text-[13px] font-medium whitespace-nowrap shrink-0 text-[#222222]"
            >
              제품소개
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-[13px] font-medium whitespace-nowrap shrink-0 cursor-pointer text-[#222222]"
            >
              오시는길
            </button>
          </div>
        </div>
      </header>

      {/* Mobile floating buttons - scroll to top + inquiry */}
      <div className="fixed right-4 bottom-6 z-50 flex md:hidden flex-col items-center gap-3">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="w-14 h-14 bg-white text-[#222222] rounded-full shadow-lg flex items-center justify-center cursor-pointer border border-gray-200"
        >
          <i className="ri-arrow-up-line text-xl"></i>
        </button>
        <Link
          to="/inquiry"
          className="w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center cursor-pointer"
        >
          <i className="ri-edit-line text-xl"></i>
        </Link>
      </div>
    </>
  );
}
